import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../utils/apiConfig';
import {
  Wallet,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  X,
  Copy,
  ExternalLink,
  Calendar,
  DollarSign,
  Hash,
  User,
  Clock,
  ChevronDown,
  Link as LinkIcon,
  TrendingUp,
  ChevronUp,
  Download,
  FileText,
  Inbox,
  Upload
} from 'lucide-react';
import { FaWallet } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';


// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const WalletServices = () => {
  const [walletServiceTypes, setWalletServiceTypes] = useState([]);
  // console.log("biwhgui5555erhtgote",walletServiceTypes)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Helper function to translate period values
  const translatePeriod = (period) => {
    const periodMap = {
      'Daily': t('dashboard.daily'),
      'Weekly': t('dashboard.weekly'),
      'Monthly': t('dashboard.monthly'),
      'Yearly': t('dashboard.yearly')
    };
    return periodMap[period] || period;
  };
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Wallet details modal removed — now navigates to detail page

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedBlockchain, setSelectedBlockchain] = useState('USDT TRC20');
  const [showBlockchainDropdown, setShowBlockchainDropdown] = useState(false);
  const [walletFormData, setWalletFormData] = useState({
    userId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [createWalletLoading, setCreateWalletLoading] = useState(false);
  const [createdWalletData, setCreatedWalletData] = useState(null);
  const [copiedWalletField, setCopiedWalletField] = useState(null);

  // Blockchain options
  const blockchainOptions = [
    { id: 'USDT TRC20', name: 'USDT TRC20', icon: 'TRC20' },
    { id: 'USDT ERC20', name: 'USDT ERC20', icon: 'ERC20' },
    // { id: 'BTC', name: 'BTC', icon: 'BTC' },
    // { id: 'ETH', name: 'ETH', icon: 'ETH' }
  ];
  // Period dropdown state
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [activePeriod, setActivePeriod] = useState('Daily');

  // Stats state
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    walletName: '',
    walletType: 'USDT-TRC20',
    walletAddress: '',
    isActive: true
  });

  // Debounce search input with 500ms delay
  const debouncedSearch = useDebounce(search, 500);

  const validateForm = () => {
    const errors = {};

    if (!walletFormData.userId.trim()) {
      errors.userId = t('Wallets.userIdRequired');
    }

    if (!walletFormData.firstName.trim()) {
      errors.firstName = t('Wallets.firstNameRequired');
    }

    if (!walletFormData.lastName.trim()) {
      errors.lastName = t('Wallets.lastNameRequired');
    }

    if (!walletFormData.email.trim()) {
      errors.email = t('Wallets.emailRequired');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(walletFormData.email)) {
        errors.email = t('Wallets.emailInvalid');
      }
    }

    if (!walletFormData.phone.trim()) {
      errors.phone = t('Wallets.phoneRequired');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (field, value) => {
    setWalletFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreatedWalletData(null);
    setCopiedWalletField(null);
    setWalletFormData({
      userId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      description: ''
    });
    setFormErrors({});
    setSelectedBlockchain('USDT TRC20');
    fetchWalletServiceTypes();
  };

  const copyWalletAddress = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedWalletField(field);
      setTimeout(() => setCopiedWalletField(null), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const handleGenerate = async () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    setCreateWalletLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/createWallet`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: walletFormData.userId,
          email: walletFormData.email,
          phone: walletFormData.phone,
          firstName: walletFormData.firstName,
          lastName: walletFormData.lastName,
          description: walletFormData.description
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        // Store created wallet data to show addresses
        setCreatedWalletData(data.data);
      } else {
        throw new Error(data.message || 'Failed to create wallet');
      }
    } catch (err) {
      console.error('Error creating wallet:', err);
    } finally {
      setCreateWalletLoading(false);
    }
  };

  // Fetch wallet stats from API with dynamic date filters
  const fetchStats = async (customFromDate = null, customToDate = null) => {
    try {
      setStatsLoading(true);
      setStatsError(null);

      // Build URL with date filters
      let url = `${API_BASE_URL}/wallet-service-types/stats`;
      const params = new URLSearchParams();

      // If custom dates are provided, use them; otherwise use activePeriod
      if (customFromDate && customToDate) {
        params.append('startDate', customFromDate);
        params.append('endDate', customToDate);
      } else {
        // Map activePeriod to API dateFilter parameter
        const dateFilterMap = {
          'Daily': 'today',
          'Weekly': 'week',
          'Monthly': 'month',
          'Yearly': 'year'
        };
        const dateFilter = dateFilterMap[activePeriod] || 'today';
        params.append('dateFilter', dateFilter);
      }

      url += `?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setStats(data.data || null);
        setStatsError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch stats');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setStatsError(err.message || 'Failed to fetch stats');
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch wallet service types from API
  const fetchWalletServiceTypes = async (currentPage = page, currentLimit = limit, searchTerm = search) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: currentLimit.toString(),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await fetch(
        `${API_BASE_URL}/wallet-service-types/list?${queryParams}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setWalletServiceTypes(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotalCount(data.pagination?.total || 0);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch wallet service types');
      }
    } catch (err) {
      console.error('Error fetching wallet service types:', err);
      setError('Failed to load wallet services');
      setWalletServiceTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletServiceTypes();
    fetchStats(); // Fetch stats on initial load
  }, []);

  // Fetch data when page, limit, or debounced search changes
  useEffect(() => {
    fetchWalletServiceTypes(page, limit, debouncedSearch);
  }, [page, limit, debouncedSearch]);

  // Fetch stats when activePeriod changes
  useEffect(() => {
    fetchStats();
  }, [activePeriod]);

  const handleAddWallet = () => {
    setFormData({
      walletName: '',
      walletType: 'USDT-TRC20',
      walletAddress: '',
      isActive: true
    });
    setShowAddModal(true);
  };

  const handleEditWallet = (wallet) => {
    setSelectedWallet(wallet);
    setFormData({
      walletName: wallet.walletName,
      walletType: wallet.walletType,
      walletAddress: wallet.walletAddress,
      isActive: wallet.isActive
    });
    setShowEditModal(true);
  };

  const handleDeleteWallet = (wallet) => {
    setSelectedWallet(wallet);
    setShowDeleteModal(true);
  };

  const handleViewWalletDetails = (serviceType) => {
    navigate(`walletbyid/${serviceType.userId}`, { state: { walletData: serviceType } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setShowAddModal(false);
    setShowEditModal(false);
    fetchWalletServiceTypes(); // Refresh the list
  };

  // Generate summary stats from API data
  const summaryStats = [
    {
      icon: FaWallet,
      title: t('Wallets.walletBalances'),
      value: stats?.walletBalances?.totalWalletBalance ? `$${stats.walletBalances.totalWalletBalance.toFixed(2)}` : '$0.00',
      iconColor: 'text-[#348BED]',
      bgColor: 'bg-blue-50',
      isLoading: statsLoading
    },
    {
      icon: FaWallet,
      title: t('Wallets.walletCreated'),
      value: stats?.walletCounts?.walletsCreated || '0',
      trend: stats?.walletCounts?.walletsCreatedChange ? `↑ ${stats.walletCounts.walletsCreatedChange} ${t('dashboard.this')} ${translatePeriod(activePeriod)}` : '',
      trendColor: 'text-green-600',
      iconColor: 'text-teal-600',
      bgColor: 'bg-teal-50',
      isLoading: statsLoading
    },
    {
      icon: FaWallet,
      title: t('Wallets.activeWallet'),
      value: stats?.walletCounts?.activeWallets || '0',
      trend: stats?.walletCounts?.activeWalletsChange ? `↑ ${stats.walletCounts.activeWalletsChange} ${t('dashboard.this')} ${translatePeriod(activePeriod)}` : '',
      trendColor: 'text-green-600',
      iconColor: 'text-[#348BED]',
      bgColor: 'bg-blue-50',
      isLoading: statsLoading
    },
    {
      icon: FaWallet,
      title: t('Wallets.totalDeposits'),
      value: stats?.deposits?.totalDeposits ? `$${stats.deposits.totalDeposits.toFixed(2)}` : '$0.00',
      trend: stats?.deposits?.totalDepositsChange ? `↑ ${stats.deposits.totalDepositsChange} ${t('dashboard.this')} ${translatePeriod(activePeriod)}` : '',
      trendColor: 'text-green-600',
      iconColor: 'text-[#348BED]',
      bgColor: 'bg-blue-50',
      isLoading: statsLoading
    },
    {
      icon: FaWallet,
      title: t('Wallets.totalWithdrawals'),
      value: stats?.withdrawals?.totalWithdrawals ? `$${stats.withdrawals.totalWithdrawals.toFixed(2)}` : '$0.00',
      trend: stats?.withdrawals?.totalWithdrawalsChange ? `↑ ${stats.withdrawals.totalWithdrawalsChange} ${t('dashboard.this')} ${translatePeriod(activePeriod)}` : '',
      trendColor: 'text-green-600',
      iconColor: 'text-[#348BED]',
      bgColor: 'bg-blue-50',
      isLoading: statsLoading
    }
  ];

  const confirmDelete = async () => {
    // Here you would implement the API call to delete the wallet

    setShowDeleteModal(false);
    setSelectedWallet(null);
    fetchWalletServiceTypes(); // Refresh the list
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here

    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const truncateAddress = (address, start = 6, end = 4) => {
    if (!address) return '';
    if (address.length <= start + end) return address;
    return `${address.substring(0, start)}...${address.substring(address.length - end)}`;
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    setPage(1); // Reset to first page when searching
  };

  // Calculate total balance for a wallet service type
  const calculateTotalBalance = (wallets) => {
    return wallets.reduce((total, wallet) => total + (wallet.totalDepositAmount || 0), 0);
  };

  // Get wallet count for display
  const getWalletCount = (wallets) => {
    return wallets.length;
  };

  // Export wallets to CSV
  const handleDownloadCSV = async () => {
    try {
      if (!walletServiceTypes || walletServiceTypes.length === 0) {
        // alert('No wallet data to export');
        return;
      }

      // Fetch all wallet data (if paginated, we might need to fetch all pages)
      let allWallets = [...walletServiceTypes];

      // If there are more pages, fetch all data
      if (totalPages > page) {
        const allPagesData = [];
        for (let p = 1; p <= totalPages; p++) {
          try {
            const queryParams = new URLSearchParams({
              page: p.toString(),
              limit: limit.toString(),
              ...(search && { search: search })
            });

            const response = await fetch(
              `${API_BASE_URL}/wallet-service-types/list?${queryParams}`,
              {
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                allPagesData.push(...data.data);
              }
            }
          } catch (err) {
            console.error(`Error fetching page ${p}:`, err);
          }
        }
        if (allPagesData.length > 0) {
          allWallets = allPagesData;
        }
      }

      // Define CSV headers
      const headers = [
        t('Wallets.csvHeaders.userName'),
        t('Wallets.csvHeaders.email'),
        t('Wallets.csvHeaders.phone'),
        t('Wallets.csvHeaders.walletAddress'),
        t('Wallets.csvHeaders.walletNetwork'),
        // t('Wallets.csvHeaders.totalBalance'),
        t('Wallets.csvHeaders.totalDeposits'),
        t('Wallets.csvHeaders.lastDepositAmount'),
        t('Wallets.csvHeaders.lastDepositDate'),
        t('Wallets.csvHeaders.status'),
        t('Wallets.csvHeaders.createdDate')
      ];

      // Convert data to CSV rows
      const csvRows = [];

      // Add header row
      csvRows.push(headers.map(header => `"${header}"`).join(','));

      // Add data rows - one row per wallet (users with multiple wallets will have multiple rows)
      allWallets.forEach((serviceType) => {
        const userInfo = serviceType.userInfo || {};
        const wallets = serviceType.wallets || [];
        // const totalBalance = calculateTotalBalance(wallets);
        const status = serviceType.isActive ? t('Wallets.active') : t('Wallets.inactive');
        const createdDate = serviceType.createdAt
          ? new Date(serviceType.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          })
          : '';

        if (wallets.length === 0) {
          // If no wallets, still create a row with user info
          const row = [
            `"${(userInfo.fullName || '').replace(/"/g, '""')}"`,
            `"${(userInfo.email || '').replace(/"/g, '""')}"`,
            `"${(userInfo.phone || '').replace(/"/g, '""')}"`,
            '""',
            '""',
            `"${totalBalance.toFixed(2)}"`,
            '"0"',
            '"0"',
            '""',
            `"${status}"`,
            `"${createdDate}"`
          ];
          csvRows.push(row.join(','));
        } else {
          // Create one row per wallet
          wallets.forEach((wallet) => {
            const row = [
              `"${(userInfo.fullName || '').replace(/"/g, '""')}"`,
              `"${(userInfo.email || '').replace(/"/g, '""')}"`,
              `"${(userInfo.phone || '').replace(/"/g, '""')}"`,
              `"${(wallet.walletAddress || '').replace(/"/g, '""')}"`,
              `"${(wallet.walletNetwork || '').replace(/"/g, '""')}"`,
              `"${totalBalance.toFixed(2)}"`,
              `"${wallet.totalDeposits || 0}"`,
              `"${(wallet.lastDepositAmount || 0).toFixed(2)}"`,
              wallet.lastDepositAt
                ? `"${new Date(wallet.lastDepositAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}"`
                : '""',
              `"${status}"`,
              `"${createdDate}"`
            ];
            csvRows.push(row.join(','));
          });
        }
      });

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Generate filename with timestamp
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/[:.]/g, '-');
      const filename = `wallets_export_${timestamp}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);


    } catch (error) {
      console.error('Error exporting CSV:', error);
      // alert('Failed to export CSV. Please try again.');
    }
  };

  // Removed full page loader - now using skeleton loaders inline

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {t('Wallets.wallets')}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {t('Wallets.walletSubtitle')}
              </p>
            </div>

            {/* Action Buttons and Filters */}
            <div className="flex flex-col items-end gap-3">
              {/* Action Buttons - Responsive */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-end w-full">
                {/* Download CSV Button */}
                <button
                  onClick={handleDownloadCSV}
                  disabled={loading || walletServiceTypes.length === 0}
                  className="flex items-center px-3 py-2 sm:px-4 bg-white border border-gray-300 rounded-[10px] text-gray-700 hover:bg-gray-50 transition-colors shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  <span className="hidden sm:inline">{t('Wallets.downloadCSV')}</span>
                  <span className="sm:hidden">CSV</span>
                </button>

                {/* Documentation Button */}
                <button
                  onClick={() => {
                    window.open("https://docs.3pa-y.com/docs/getting-started", "_blank");
                  }}
                  className="flex items-center px-3 py-2 sm:px-4 bg-white border border-gray-300 rounded-[10px] text-gray-700 hover:bg-gray-50 transition-colors shadow-sm text-sm">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  <span className="hidden sm:inline">{t('Wallets.documentation')}</span>
                  <span className="sm:hidden">Docs</span>
                </button>

                {/* Create new Payments Button */}
                <button
                  onClick={() => {
                    setShowCreateModal(true);
                    setWalletFormData({
                      userId: '',
                      firstName: '',
                      lastName: '',
                      email: '',
                      phone: '',
                      description: ''
                    });
                    setFormErrors({});
                  }}
                  style={{
                    background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                    borderRadius: "10px"
                  }}
                  className="flex items-center px-3 py-2 sm:px-4 text-white rounded-lg hover:opacity-90 transition-colors text-sm">
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  <span className="hidden sm:inline">{t('Wallets.createNewWallet')}</span>
                  <span className="sm:hidden">Create</span>
                </button>
              </div>

              {/* Separate Filter Controls */}
              <div className="flex items-center gap-3">
                {/* Date Range Picker - Separate Box */}
                <div className="relative bg-white border border-gray-300 rounded-lg shadow-sm">
                    <div className="relative date-picker">
                    <button
                      onClick={() => {

                        setShowDatePicker(!showDatePicker);
                      }}
                      className="date-picker-button flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {new Date(fromDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      -{" "}
                      {new Date(toDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </button>

                    {/* Date Picker Dropdown */}
                    {showDatePicker && (
                      <div className="absolute right-0 lg:right-0 left-0 lg:left-auto mt-2 w-80 sm:w-96 lg:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-gray-900">
                            {t('Wallets.selectDateRange')}
                          </h3>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {t('Wallets.fromDate')}
                              </label>
                              <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {t('Wallets.toDate')}
                              </label>
                              <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                            <button
                              onClick={() => setShowDatePicker(false)}
                              className="w-full sm:w-auto px-3 py-2 text-xs text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg"
                            >
                              {t('common.cancel')}
                            </button>
                            <button
                              onClick={() => {
                                setShowDatePicker(false);
                                // Fetch stats with custom date range
                                fetchStats(fromDate, toDate);
                                fetchWalletServiceTypes();
                              }}
                              className="w-full sm:w-auto px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              {t('common.apply')}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Period Dropdown - Separate Box */}
                <div className="relative bg-white border border-gray-300 rounded-lg shadow-sm">
                  <div className="relative period-dropdown">
                    <button
                      onClick={() => {

                        setShowPeriodDropdown(!showPeriodDropdown);
                      }}
                      className="period-button flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      {translatePeriod(activePeriod)}
                      <svg
                        className={`w-4 h-4 transition-transform ${showPeriodDropdown ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {showPeriodDropdown && (
                      <div onClick={(e) => e.stopPropagation()} className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-2xl shadow-lg z-50">
                        <button
                          onClick={() => {
                            setActivePeriod("Daily");
                            setShowPeriodDropdown(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 rounded-t-2xl"
                        >
                          {t('dashboard.daily')}
                        </button>
                        <button
                          onClick={() => {
                            setActivePeriod("Weekly");
                            setShowPeriodDropdown(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
                        >
                          {t('dashboard.weekly')}
                        </button>
                        <button
                          onClick={() => {
                            setActivePeriod("Monthly");
                            setShowPeriodDropdown(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
                        >
                          {t('dashboard.monthly')}
                        </button>
                        <button
                          onClick={() => {
                            setActivePeriod("Yearly");
                            setShowPeriodDropdown(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 rounded-b-2xl"
                        >
                          {t('dashboard.yearly')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reset Filter Button */}
                <button
                  onClick={() => {
                    // Reset all filters to default values
                    setFromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                    setToDate(new Date().toISOString().split('T')[0]);
                    setActivePeriod('Daily');
                    setSearch('');
                    setPage(1);
                    // Refetch data with default filters
                    fetchStats();
                    fetchWalletServiceTypes(1, limit, '');
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('Wallets.reset')}
                </button>

                {/* Filter Button */}
                {/* <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors shadow-sm">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  Filter
                </button> */}
                {/* <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                   <Calendar className="w-4 h-4 mr-2" />
                                   Oct 18 - Nov 18
                               </button>
   
                               <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                   Monthly
                                   <ChevronDown className="w-4 h-4 ml-2" />
                               </button>
   
                               <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                   <Filter className="w-4 h-4 mr-2" />
                                   Filter
                               </button> */}
              </div>
            </div>
          </div>
        </div>
        {/* Summary Statistics Cards */}
        <div className="flex flex-wrap gap-0 bg-white rounded-2xl shadow-sm mb-6 overflow-hidden">
          {statsLoading ? (
            // Skeleton Loaders for Summary Cards
            <>
              {[...Array(5)].map((_, index) => (
                <React.Fragment key={`skeleton-stat-${index}`}>
                  <div className="flex-1 min-w-[240px] px-4 py-4">
                    <div className="flex items-center gap-3">
                      {/* Icon skeleton */}
                      <div className="bg-gray-200 rounded-lg p-2.5 flex-shrink-0 w-12 h-12 animate-pulse"></div>

                      {/* Content skeleton */}
                      <div className="flex-1 min-w-0">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-2"></div>
                        <div className="flex items-center gap-2">
                          <div className="h-6 bg-gray-200 rounded animate-pulse w-20"></div>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Divider line between cards (except last) */}
                  {index < 4 && (
                    <div className="w-px bg-gray-200 self-stretch"></div>
                  )}
                </React.Fragment>
              ))}
            </>
          ) : (
            summaryStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <React.Fragment key={index}>
                  <div className="flex-1 min-w-[240px] px-4 py-4">
                    <div className="flex items-center gap-3">
                      {/* Icon in colored rounded square */}
                      <div className={`${stat.bgColor} rounded-lg p-2.5 flex-shrink-0`}>
                        <Icon className={`w-7 h-7 ${stat.iconColor}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">{stat.title}</p>

                        <div className="flex items-center gap-2">
                          <p className="text-xl font-bold text-gray-800">{stat.value}</p>

                          {stat.trend && (
                            <div className="flex items-center gap-1">
                              {/* <TrendingUp className={`w-3 h-3 ${stat.trendColor}`} /> */}
                              <span className={`text-xs ${stat.trendColor} font-medium`}>
                                {stat.trend}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Divider line between cards (except last) */}
                  {index < summaryStats.length - 1 && (
                    <div className="w-px bg-gray-200 self-stretch"></div>
                  )}
                </React.Fragment>
              );
            })
          )}
        </div>
        {/* Search Filter */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t('Wallets.searchPlaceholder')}
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full px-4 py-3 pl-10 bg-white border border-gray-200 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-400 shadow-sm"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
            {search && (
              <button
                onClick={() => handleSearch('')}
                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('Wallets.clear')}
              </button>
            )}
          </div>
        </div>

        {/* Wallets Table */}
        <div className="bg-white rounded-[25px] shadow overflow-hidden">
          {loading ? (
            <div className="overflow-x-auto p-2">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                      {t('Wallets.userInformation')}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                      {t('Wallets.wallets')}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                      {t('Wallets.depositCount')}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                      {t('Wallets.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                      {t('Wallets.created')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[...Array(6)].map((_, index) => (
                    <tr key={`skeleton-${index}`} className="animate-pulse">
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 bg-gray-200 rounded-full w-40"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : walletServiceTypes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Inbox className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('Wallets.noDataFound')}</h3>
              <p className="text-sm text-gray-500 max-w-md">
                {t('Wallets.noDataMessage')}              </p>
            </div>
          ) : (
            <div className="overflow-x-auto p-2">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                      {t('Wallets.userInformation')}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                      {t('Wallets.wallets')}
                    </th>

                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                      {t('Wallets.depositCount')}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                      {t('Wallets.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                      {t('Wallets.created')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {walletServiceTypes.map((serviceType) => (
                    <tr
                      key={serviceType._id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                      onClick={() => handleViewWalletDetails(serviceType)}
                    >
                      {/* User Information */}
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">

                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {serviceType.userInfo?.fullName || '--'}
                            </div>
                            {/* <div className="text-xs text-gray-500">
                            {serviceType.userInfo?.email || 'N/A'}
                          </div> */}
                          </div>
                        </div>
                      </td>

                      {/* Wallets */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">

                          <span className="inline-flex px-2 py-1 text-xs font-medium  rounded-full">
                            {serviceType?.wallets?.[0]?.walletAddress
                              ? `${serviceType.wallets[0].walletAddress.slice(0, 10)}...${serviceType.wallets[0].walletAddress.slice(-6)}`
                              : ""}
                          </span>


                        </div>
                      </td>

                      {/* Deposit Count */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(serviceType.wallets || []).reduce((sum, w) => sum + (w.totalDeposits || 0), 0)}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${serviceType.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {serviceType.isActive ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {t('Wallets.active')}
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3 mr-1" />
                              {t('Wallets.inactive')}
                            </>
                          )}
                        </span>
                      </td>

                      {/* Created */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(serviceType.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className=" px-6 py-3  flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-gray-700">
              <span className="hidden sm:inline">
                {t('Wallets.showingResults', { from: ((page - 1) * limit) + 1, to: Math.min(page * limit, totalCount), total: totalCount })}
              </span>
              <span className="sm:hidden">
                {t('Wallets.showingResultsMobile', { from: ((page - 1) * limit) + 1, to: Math.min(page * limit, totalCount), total: totalCount })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('Wallets.previous')}
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                {t('Wallets.page')} {page} {t('Wallets.of')} {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('Wallets.next')}
              </button>
            </div>
          </div>
        )}




        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#F6F7F8] rounded-2xl shadow-xl max-w-[30rem] w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-[#F6F7F8] z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  {createdWalletData ? t('Wallets.walletCreated') || 'Wallet Created' : t('Wallets.createWallet')}
                </h2>
                <button
                  onClick={handleCloseCreateModal}
                  className="p-1 bg-[#D1D5DB87] hover:bg-[#D1D5DB87] rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {createdWalletData ? (
                /* Success View — Show generated wallet addresses */
                <div className="p-6 space-y-4 bg-[#F6F7F8]">
                  <div className="flex flex-col items-center mb-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('Wallets.walletCreatedSuccessfully') || 'Wallet Created Successfully!'}</h3>
                    <p className="text-sm text-gray-500 text-center mt-1">
                      {t('Wallets.walletCreatedDescription') || 'Both TRC20 and ERC20 wallets have been generated. Please save the addresses below.'}
                    </p>
                  </div>

                  {/* Display wallet addresses */}
                  {createdWalletData.wallets && createdWalletData.wallets.length > 0 ? (
                    createdWalletData.wallets.map((wallet, index) => (
                      <div key={index} className="bg-white rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <img src={wallet.walletNetwork === "USDT-TRC20" ? '/TRC20.png' : '/USDT-ERC20.png'} alt='chain' className='w-5 h-5' />
                          <span className="text-sm font-semibold text-gray-900">{wallet.walletNetwork}</span>
                        </div>
                        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                          <span className="text-xs font-mono text-gray-700 break-all pr-2">{wallet.walletAddress}</span>
                          <button
                            onClick={() => copyWalletAddress(wallet.walletAddress, `wallet-${index}`)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                          >
                            {copiedWalletField === `wallet-${index}` ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    /* Fallback: show raw data fields if wallets array not present */
                    <>
                      {createdWalletData.trc20Address && (
                        <div className="bg-white rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <img src='/TRC20.png' alt='TRC20' className='w-5 h-5' />
                            <span className="text-sm font-semibold text-gray-900">USDT-TRC20</span>
                          </div>
                          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                            <span className="text-xs font-mono text-gray-700 break-all pr-2">{createdWalletData.trc20Address}</span>
                            <button
                              onClick={() => copyWalletAddress(createdWalletData.trc20Address, 'trc20')}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                            >
                              {copiedWalletField === 'trc20' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      )}
                      {createdWalletData.erc20Address && (
                        <div className="bg-white rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <img src='/USDT-ERC20.png' alt='ERC20' className='w-5 h-5' />
                            <span className="text-sm font-semibold text-gray-900">USDT-ERC20</span>
                          </div>
                          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                            <span className="text-xs font-mono text-gray-700 break-all pr-2">{createdWalletData.erc20Address}</span>
                            <button
                              onClick={() => copyWalletAddress(createdWalletData.erc20Address, 'erc20')}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                            >
                              {copiedWalletField === 'erc20' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Done button */}
                  <div className="pt-2">
                    <button
                      onClick={handleCloseCreateModal}
                      style={{
                        background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                        borderRadius: "10px"
                      }}
                      className="w-full text-white py-3 rounded-lg font-medium hover:opacity-90 transition-colors"
                    >
                      {t('Wallets.done') || 'Done'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Form View — Create wallet form */
                <>
                  {/* Modal Content */}
                  <div className="p-6 space-y-4 bg-[#F6F7F8]">
                    <p className="text-sm text-[#1A1C1E] mb-2">
                      {t('Wallets.createWalletDescription')}
                    </p>

                    {/* Info note about both networks — Fix 1.8 */}
                    <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-sm text-blue-700">
                        A wallet will be generated for both TRC20 and ERC20 networks automatically.
                      </span>
                    </div>

                    {/* User ID */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[#1A1C1E]">
                        {t('Wallets.userId')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={walletFormData?.userId}
                        onChange={(e) => handleFormChange('userId', e.target.value)}
                        placeholder={t('Wallets.enterUserId')}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl  text-sm focus:outline-none focus:ring-0 focus:border-gray-400"
                      />
                      {formErrors.userId && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.userId}</p>
                      )}
                    </div>

                    {/* First Name and Last Name - Two columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* First Name */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-[#1A1C1E]">
                          {t('Wallets.firstName')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={walletFormData.firstName}
                          onChange={(e) => handleFormChange('firstName', e.target.value)}
                          placeholder={t('Wallets.enterFirstName')}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl  text-sm focus:outline-none focus:ring-0 focus:border-gray-400"
                        />
                        {formErrors.firstName && (
                          <p className="text-sm text-red-500 mt-1">{formErrors.firstName}</p>
                        )}
                      </div>

                      {/* Last Name */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-[#1A1C1E]">
                          {t('Wallets.lastName')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={walletFormData.lastName}
                          onChange={(e) => handleFormChange('lastName', e.target.value)}
                          placeholder={t('Wallets.enterLastName')}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl  text-sm focus:outline-none focus:ring-0 focus:border-gray-400"
                        />
                        {formErrors.lastName && (
                          <p className="text-sm text-red-500 mt-1">{formErrors.lastName}</p>
                        )}
                      </div>
                    </div>

                    {/* Email and Phone - Two columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Email Address */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-[#1A1C1E]">
                          {t('Wallets.emailAddress')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={walletFormData.email}
                          onChange={(e) => handleFormChange('email', e.target.value)}
                          placeholder={t('Wallets.enterEmailAddress')}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl  text-sm focus:outline-none focus:ring-0 focus:border-gray-400"
                        />
                        {formErrors.email && (
                          <p className="text-sm text-red-500 mt-1">{formErrors.email}</p>
                        )}
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-[#1A1C1E]">
                          {t('Wallets.phone')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          value={walletFormData.phone}
                          onChange={(e) => handleFormChange('phone', e.target.value)}
                          placeholder={t('Wallets.enterPhoneNumber')}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl  text-sm focus:outline-none focus:ring-0 focus:border-gray-400"
                        />
                        {formErrors.phone && (
                          <p className="text-sm text-red-500 mt-1">{formErrors.phone}</p>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[#1A1C1E]">
                        {t('Wallets.description')}
                      </label>
                      <textarea
                        value={walletFormData.description}
                        onChange={(e) => handleFormChange('description', e.target.value)}
                        placeholder={t('Wallets.enterDescription')}
                        rows="4"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl  text-sm focus:outline-none focus:ring-0 focus:border-gray-400"
                      />
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="p-6 border-t border-gray-200 sticky bottom-0 bg-[#F6F7F8]">
                    <button
                      onClick={handleGenerate}
                      disabled={createWalletLoading}
                      style={{
                        background: createWalletLoading ? "#94a3b8" : "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                        borderRadius: "10px"
                      }}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-70 flex items-center justify-center"
                    >
                      {createWalletLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('Wallets.creatingWallet')}
                        </>
                      ) : (
                        t('Wallets.generateWallet')
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletServices;
